import { useMemo } from "react";
import { FileText, Hash } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../../ui/command";
import type { DocSection, DocHeading } from "../../types";

type SearchResult =
  | { type: "section"; section: DocSection }
  | { type: "heading"; section: DocSection; heading: DocHeading }
  | { type: "content"; section: DocSection; snippet: string };

interface DocsSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (sectionId: string, headingId?: string) => void;
  docSections: DocSection[];
}

function getSnippet(
  content: string,
  query: string,
  contextLength = 60,
): string {
  const lowerContent = content.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerContent.indexOf(lowerQuery);

  if (index === -1) return "";

  const start = Math.max(0, index - contextLength);
  const end = Math.min(content.length, index + query.length + contextLength);

  let snippet = content.slice(start, end);

  snippet = snippet
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`[^`]+`/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[#*_~]/g, "")
    .replace(/\n+/g, " ")
    .trim();

  const prefix = start > 0 ? "..." : "";
  const suffix = end < content.length ? "..." : "";

  return `${prefix}${snippet}${suffix}`;
}

export function DocsSearch({
  open,
  onOpenChange,
  onNavigate,
  docSections,
}: DocsSearchProps) {
  const handleSelect = (result: SearchResult) => {
    if (result.type === "heading") {
      onNavigate(result.section.id, result.heading.id);
    } else {
      onNavigate(result.section.id);
    }
    onOpenChange(false);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search Documentation"
      description="Search through all documentation pages and headings"
      showCloseButton={false}
    >
      <CommandInput placeholder="Search documentation..." />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Pages">
          {docSections.map((section) => (
            <CommandItem
              key={`section-${section.id}`}
              value={`section ${section.title}`}
              onSelect={() => handleSelect({ type: "section", section })}
              className="flex items-center gap-3 cursor-pointer"
            >
              <FileText className="h-4 w-4 text-zinc-400 shrink-0" />
              <span>{section.title}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandGroup heading="Headings">
          {docSections.flatMap((section) =>
            section.headings.map((heading) => (
              <CommandItem
                key={`heading-${section.id}-${heading.id}`}
                value={`heading ${heading.text} ${section.title}`}
                onSelect={() =>
                  handleSelect({ type: "heading", section, heading })
                }
                className="flex items-center gap-3 cursor-pointer"
              >
                <Hash className="h-4 w-4 text-zinc-400 shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="truncate">{heading.text}</span>
                  <span className="text-xs text-zinc-500 truncate">
                    in {section.title}
                  </span>
                </div>
              </CommandItem>
            )),
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
